<script setup lang="ts">
  import { onMounted, ref, computed } from "vue";
  import { useRouter } from "vue-router";
  import {
    Text,
    Tile,
    Tag,
    IconArrowLeft,
    IconMobile,
    IconOverflowMenu,
    IconBarcodeScanner,
    IconRfidReader,
    Button,
  } from "@ui-kit";
  import { StatusBar } from "@src/components";
  import { useDeviceInfo } from "@plugins";

  const { deviceInfo } = useDeviceInfo();
  const { go } = useRouter();

  const hasNewVersion = ref(false);

  const goBack = () => go(-1);
  const handleReload = () => {
    window.location.reload();
  };

  const getScreenResolution = () => {
    return `${window.screen.width} x ${window.screen.height}`;
  };

  //@ts-ignore
  const appVersion = import.meta.env.VITE_APP_VERSION || "unknown";
  const fullVersion = computed(() => `spa: ${appVersion}, apk: ${deviceInfo.value.apkVersion || "unknown"}`);

  onMounted(async () => {
    fetch(window.location.origin + "/version-info.json", {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        hasNewVersion.value = data.version !== appVersion;
      });
  });
</script>

<template>
  <div class="device-info-page" data-testid="device-info">
    <StatusBar />
    <div class="device-info-page__back">
      <IconArrowLeft @click="goBack" data-testid="device-info-back-btn" />
      <Text font="body-medium-16">{{ $t("Об устройстве") }}</Text>
      <IconOverflowMenu />
    </div>
    <div class="device-info-page__content">
      <Tile class="device-info-page__tile">
        <IconMobile />
        <div class="device-info-page__description">
          <Text font="body-regular-16">{{ $t("Смартфон") }}</Text>
          <Text font="body-regular-14" color="secondary">{{ deviceInfo?.model }}</Text>
        </div>
        <Tag :text="$t('Заряд') + ' ' + (deviceInfo?.batteryLevel || '~') + '%'" variant="success" />
      </Tile>
      <Tile class="device-info-page__tile">
        <IconBarcodeScanner />
        <div class="device-info-page__description">
          <Text font="body-regular-16">{{ $t("Сканер штрих-кодов") }}</Text>
          <Text font="body-regular-14" color="secondary">...</Text>
        </div>
        <Tag
          :text="deviceInfo?.isBarcodeScanner ? $t('Активен') : $t('Недоступен')"
          :variant="deviceInfo?.isBarcodeScanner ? 'success' : 'neutral'"
        />
      </Tile>
      <Tile class="device-info-page__tile">
        <IconRfidReader />
        <div class="device-info-page__description">
          <Text font="body-regular-16">{{ $t("Считыватель RFID меток") }}</Text>
          <Text font="body-regular-14" color="secondary">...</Text>
        </div>
        <Tag
          :text="deviceInfo?.isRfidReader ? $t('Активен') : $t('Недоступен')"
          :variant="deviceInfo?.isRfidReader ? 'success' : 'neutral'"
        />
      </Tile>
      <Tile class="device-info-page__tile">
        <Text>{{ $t("Разрешение") }}: {{ getScreenResolution() }}</Text>
      </Tile>
    </div>
    <div class="device-info-page__footer" data-testid="app-version">
      <Text color="secondary" font="body-regular-12">{{ fullVersion }}</Text>
      <Button v-if="hasNewVersion" @click="handleReload">{{ $t("Обновить приложение") }}</Button>
    </div>
  </div>
</template>

<style scoped>
  .device-info-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--sf-base-background-primary);
  }

  .device-info-page__update-btn {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  .device-info-page__back {
    display: flex;
    justify-content: space-between;
    padding: 7px 20px;
  }

  .device-info-page__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    padding: 22px 16px 0;
  }

  .device-info-page__tile {
    display: flex;
    gap: 8px;
    padding: 8px 12px;
    align-items: center;
  }

  .device-info-page__description {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 2px;
  }

  .device-info-page__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
    padding: 12px 16px;
  }

  .device-info-page__footer :deep(.button) {
    width: 100%;
  }
</style>
